import type {
  CompanyInfo,
  UpdateCompanyPayload,
} from '../api/hooks/useCompanyInfo';

export interface ParsedAddressComponents {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

/**
 * Extracts structured address parts from Google Places address_components.
 * Falls back to empty strings for any part that isn't present.
 */
export const parseAddressComponents = (
  components: google.maps.GeocoderAddressComponent[]
): ParsedAddressComponents => {
  const get = (type: string) =>
    components.find((c) => c.types.includes(type))?.long_name ?? '';
  const getShort = (type: string) =>
    components.find((c) => c.types.includes(type))?.short_name ?? '';

  const streetNumber = get('street_number');
  const route = get('route');
  const streetAddress = [streetNumber, route].filter(Boolean).join(' ');

  return {
    streetAddress,
    city: get('locality') || get('sublocality') || get('postal_town'),
    state: getShort('administrative_area_level_1'),
    zipCode: get('postal_code'),
  };
};

export interface CompanyAddressSnapshot {
  name: string;
  companyPhone: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

export const formatCompanyAddress = (
  streetAddress?: string,
  city?: string,
  state?: string,
  zipCode?: string
): string =>
  [streetAddress, city, state, zipCode]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

export const snapshotCompanyFields = (
  info: CompanyInfo
): CompanyAddressSnapshot => ({
  name: info.name ?? '',
  companyPhone: info.companyPhone ?? '',
  streetAddress: info.streetAddress ?? '',
  city: info.city ?? '',
  state: info.state ?? '',
  zipCode: info.zipCode ?? '',
});

/**
 * Builds the PATCH payload according to plan rules:
 * - If addressLine is unchanged from the formatted initial address,
 *   send the original structured fields to avoid flattening structured data.
 * - If the user edited addressLine, send it as streetAddress and clear the
 *   sub-fields so we don't send stale structured data.
 */
export const buildUpdateBody = (
  name: string,
  companyPhone: string,
  addressLine: string,
  initial: CompanyAddressSnapshot
): UpdateCompanyPayload => {
  const initialFormatted = formatCompanyAddress(
    initial.streetAddress,
    initial.city,
    initial.state,
    initial.zipCode
  );

  const addressUnchanged = addressLine.trim() === initialFormatted;

  if (addressUnchanged) {
    return {
      name: name.trim(),
      companyPhone: companyPhone.trim(),
      streetAddress: initial.streetAddress,
      city: initial.city,
      state: initial.state,
      zipCode: initial.zipCode,
    };
  }

  return {
    name: name.trim(),
    companyPhone: companyPhone.trim(),
    streetAddress: addressLine.trim(),
    city: '',
    state: '',
    zipCode: '',
  };
};
